<template>
  <div class="ppv-track-section">
    <div
      class="ppv-track-header"
      @click="toggleTrack"
    >
      <div class="ppv-track-header-left">
        <div class="ppv-track-icon-wrap">
          <v-icon class="ppv-track-icon">{{ trackIcon }}</v-icon>
        </div>
        <span class="ppv-track-name">{{ trackLabel }}</span>
      </div>

      <v-icon
        class="ppv-track-chevron"
        :class="{ open: !isCollapsed }"
      >
        {{ icons.mdiChevronDown }}
      </v-icon>
    </div>

    <div v-if="!isCollapsed" class="ppv-track-body">
      <table class="ppv-table">
        <thead>
          <tr>
            <th class="ppv-th ppv-th-condition">{{ $t("ppv.insights.th_condition") }}</th>
            <th class="ppv-th ppv-th-category">{{ $t("ppv.insights.th_category") }}</th>
            <th class="ppv-th ppv-th-right ppv-th-count">{{ $t("ppv.insights.th_risk_count") }}</th>
          </tr>
        </thead>

        <ppv-assessment-row
          v-for="a in trackGroup.assessments"
          :key="a.name"
          :assessment="a"
          :is-expanded="!!expandedAssessments[trackGroup.track + ':' + a.name]"
          :on-toggle-expand="toggleAssessment"
        />

        <tbody>
          <tr class="ppv-total-row">
            <td class="ppv-td ppv-total-label">
              {{ $t("ppv.insights.total") }}
            </td>
            <td class="ppv-td ppv-td-category" />
            <td class="ppv-td ppv-td-right ppv-total-count">
              {{ trackGroup.total }}
            </td>
          </tr>
        </tbody>

        <ppv-negatives-section
          v-if="trackGroup.negatives.length"
          :negatives="trackGroup.negatives"
          :is-expanded="!!expandedNegatives"
          :track-name="trackGroup.track"
          :on-toggle="toggleNegatives"
        />
      </table>
    </div>
  </div>
</template>

<script>
import icons from '@/mixins/icons'
import PpvAssessmentRow from './PpvAssessmentRow'
import PpvNegativesSection from './PpvNegativesSection'

export default {
  name: 'PpvTrackSection',
  components: { PpvAssessmentRow, PpvNegativesSection },
  mixins: [icons],
  props: {
    trackGroup: {
      type: Object,
      required: true
    },
    isCollapsed: {
      type: Boolean,
      default: false
    },
    expandedAssessments: {
      type: Object,
      default: () => ({})
    },
    expandedNegatives: {
      type: Boolean,
      default: false
    },
    trackOptions: {
      type: Array,
      default: () => []
    },
  },
  emits: ['toggle-track', 'toggle-expand', 'toggle-negatives'],
  computed: {
    trackIcon() {
      const opt = this.trackOptions.find(o => o.value === this.trackGroup.track)

      return opt ? opt.icon : this.icons.mdiAccount
    },
    trackLabel() {
      const opt = this.trackOptions.find(o => o.value === this.trackGroup.track)

      return opt ? this.$t(opt.label) : this.trackGroup.track
    },
  },
  methods: {
    toggleTrack() {
      this.$emit('toggle-track', this.trackGroup.track)
    },
    toggleAssessment(assessment) {
      this.$emit('toggle-expand', assessment)
    },
    toggleNegatives() {
      this.$emit('toggle-negatives', this.trackGroup.track)
    },
  },
}
</script>

<style scoped>
.ppv-track-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #eaedf2;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  overflow: hidden;
  margin-bottom: 16px;
}
.ppv-track-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  cursor: pointer;
  user-select: none;
  transition: background .12s;
}
.ppv-track-header:hover {
  background: #f8fafc;
}
.ppv-track-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.ppv-track-icon-wrap {
  width: 48px;
  height: 48px;
  background: #fff;
  border: 1px solid #eaedf2;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ppv-track-icon {
  color: #4b91ac !important;
  font-size: 20px !important;
}
.ppv-track-name {
  font-size: 15px;
  font-weight: 700;
  color: #1a2340;
}
.ppv-track-chevron {
  color: #8a96a8 !important;
  font-size: 20px !important;
  transition: transform .2s;
}
.ppv-track-chevron.open {
  transform: rotate(180deg);
}
.ppv-track-body {
  border-top: 1px solid #eaedf2;
}
.ppv-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.ppv-th {
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 700;
  color: #8a96a8;
  letter-spacing: .06em;
  text-align: left;
  border-bottom: 1px solid #eaedf2;
  background: #f8f9fb;
  text-transform: uppercase;
}
.ppv-th-right {
  text-align: right;
}
.ppv-th-condition {
  width: 45%;
}
.ppv-th-category {
  width: 40%;
}
.ppv-th-count {
  width: 15%;
}
@media (max-width: 800px) {
  .ppv-th-category,
  .ppv-td-category {
    display: none;
  }
  .ppv-th-condition {
    width: 75%;
  }
  .ppv-th-count {
    width: 25%;
  }
}
.ppv-td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
  vertical-align: middle;
  word-break: break-word;
}
.ppv-td-right {
  text-align: right;
  white-space: nowrap;
  vertical-align: middle;
}
.ppv-total-row {
  background: #f8f9fb;
}
.ppv-total-label {
  font-size: 12px;
  font-weight: 700;
  color: #1a2340;
  letter-spacing: .04em;
  text-transform: uppercase;
  border-top: 1.5px solid #eaedf2 !important;
}
.ppv-total-count {
  font-size: 14px;
  font-weight: 700;
  color: #1a2340;
  border-top: 1.5px solid #eaedf2 !important;
}
</style>
